import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useWeekNav } from '../contexts/WeekNavContext';
import { getWeekDates, toISO, formatDate } from '../utils/dateUtils';
import { getSubjectStyle } from '../utils/subjectStyles';
import { getClassStyle } from '../utils/classStyles';

const DAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

const toMin = hhmm => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

// The "active" lesson = first lesson today whose END is still in the future.
// In a break this resolves to the next lesson; before school → the first; after
// the last lesson → none. Needs configured lessonTimes.
function activeLessonId(dayLessons, lessonTimes, now) {
  if (!lessonTimes.length) return null;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const cand = dayLessons.map(l => {
    const si = l.slotIndex ?? 0, dur = l.duration || 1;
    const t0 = lessonTimes[si], t1 = lessonTimes[si + dur - 1] || t0;
    return t0 ? { id: l.id, start: toMin(t0.start), end: toMin(t1.end) } : null;
  }).filter(Boolean).filter(x => x.end > nowMin).sort((a, b) => a.start - b.start);
  return cand[0]?.id ?? null;
}

function SubjectChip({ subjectId, subjects }) {
  const s = getSubjectStyle(subjectId, subjects);
  const name = subjects.find(sub => sub.id === subjectId)?.name || 'Allgemein';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 100,
      fontSize: 11.5, fontWeight: 600, letterSpacing: '0.01em',
      background: s.tint, color: s.ink, whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

function Empty({ text }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>{text}</div>
  );
}

// Colour-coded, per-class badge (each class gets a distinct stable colour).
function ClassBadge({ classId, classes, size = 'sm' }) {
  const st = getClassStyle(classId, classes);
  if (!st) return null;
  const big = size === 'md';
  return (
    <span className="mono" style={{
      display: 'inline-flex', alignItems: 'center',
      padding: big ? '3px 10px' : '2px 7px', borderRadius: 100,
      fontSize: big ? 11.5 : 10, fontWeight: 700, letterSpacing: '0.01em',
      background: st.tint, color: st.ink,
      border: `1px solid color-mix(in srgb, ${st.ink} 35%, transparent)`,
      whiteSpace: 'nowrap',
    }}>
      {st.name}
    </span>
  );
}

// Compact multi-select filter: a tab-row pill that opens a popover of toggleable,
// color-highlighted rows (no checkboxes). Selection is shown by a colored swatch
// + highlighted row.
function FilterDropdown({ label, options, selected, onToggle, colorFor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const count = selected.length;
  return (
    <div ref={ref} data-no-print style={{ position: 'relative', display: 'inline-flex' }}>
      {/* trigger — styled as a .tab-row pill (parent bubble) */}
      <button type="button" className={count ? 'active' : ''} onClick={() => setOpen(o => !o)}>
        {label}{count ? ` (${count})` : ''} <span style={{ fontSize: 8, opacity: 0.6 }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 90,
          background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 'var(--r-m)',
          boxShadow: 'var(--sh-2)', padding: 6, minWidth: 160, maxHeight: 320, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {options.map(o => {
            const checked = selected.includes(o.id);
            const ink = colorFor(o.id);
            return (
              <div
                key={o.id}
                role="button"
                onClick={() => onToggle(o.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px',
                  borderRadius: 'var(--r-s)', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                  background: checked ? `color-mix(in srgb, ${ink} 16%, var(--paper))` : 'transparent',
                  color: checked ? ink : 'var(--ink-1)', fontWeight: checked ? 600 : 400,
                }}
              >
                <span style={{
                  width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: ink, opacity: checked ? 1 : 0.35,
                }} />
                {o.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Read-only week grid, mirrors the teacher WochenrasterView layout (no edit/drag/create).
function WeekGrid({ gridLessons, weekDates, subjects, classes, slotCount, lessonTimes, isMobile, onSelectLesson, activeId }) {
  const slots = Array.from({ length: slotCount }, (_, i) => i);

  function isCovered(di, si) {
    if (si === 0) return false;
    return gridLessons.some(l => l.dayIndex === di && l.slotIndex === si - 1 && (l.duration || 1) >= 2);
  }

  return (
    <div className="card-flat scroll-x" style={{ padding: 0, overflow: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(5, 1fr)', minWidth: 560 }}>
        <div style={{ gridRow: 1, gridColumn: 1, borderBottom: '1px solid var(--line)' }} />
        {DAYS_LONG.map((d, i) => (
          <div key={d} style={{ gridRow: 1, gridColumn: i + 2, padding: '10px 12px', borderBottom: '1px solid var(--line)', borderLeft: '1px solid var(--line-soft)', textAlign: 'center' }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{formatDate(weekDates[i])}</div>
            <div style={{ fontWeight: 600, fontSize: 13, marginTop: 1 }}>{isMobile ? d.slice(0, 2) : d}</div>
          </div>
        ))}
        {slots.map((si) => (
          <React.Fragment key={si}>
            <div className="mono" style={{
              gridRow: si + 2, gridColumn: 1,
              fontSize: 9, color: 'var(--ink-3)', padding: '6px 4px',
              borderBottom: si < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
            }}>
              {lessonTimes[si]
                ? <><span>{lessonTimes[si].start}</span><span style={{ color: 'var(--ink-4)' }}>{lessonTimes[si].end}</span></>
                : <span>{si + 1}</span>}
            </div>
            {Array.from({ length: 5 }, (_, di) => {
              if (isCovered(di, si)) return null;
              const lesson   = gridLessons.find(l => l.dayIndex === di && l.slotIndex === si);
              const duration = lesson?.duration || 1;
              return (
                <div key={di} style={{
                  gridRow: duration >= 2 ? `${si + 2} / span 2` : si + 2,
                  gridColumn: di + 2,
                  padding: 6, minHeight: 64,
                  borderLeft: '1px solid var(--line-soft)',
                  borderBottom: (si + duration - 1) < slots.length - 1 ? '1px solid var(--line-soft)' : 'none',
                }}>
                  {lesson && (() => {
                    const s = getSubjectStyle(lesson.subjectId, subjects);
                    const className = classes.find(c => c.id === lesson.classId)?.name;
                    const isActive = lesson.id === activeId;
                    const isGhost  = lesson.isGhost;
                    return (
                      <div
                        onClick={isGhost ? undefined : () => onSelectLesson(lesson)}
                        style={{
                          background: isGhost ? 'transparent' : s.tint, padding: '6px 8px', borderRadius: 6,
                          ...(isGhost
                            ? { border: `1px dashed color-mix(in srgb, ${s.ink} 45%, var(--line))`, borderLeft: `3px solid color-mix(in srgb, ${s.ink} 55%, transparent)` }
                            : { borderLeft: `3px solid ${s.ink}` }),
                          height: '100%', cursor: isGhost ? 'default' : 'pointer', opacity: isGhost ? 0.8 : 1,
                          boxShadow: isActive ? '0 0 0 2px #d20f39' : undefined,
                          position: isActive ? 'relative' : undefined, zIndex: isActive ? 1 : undefined,
                        }}
                      >
                        <div className="mono" style={{ fontSize: 9, marginBottom: 2, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{ fontWeight: 700, color: s.ink }}>{s.short}</span>
                          {className && <span style={{ fontWeight: 500, color: 'var(--ink-3)' }}>{className}</span>}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ink-1)', lineHeight: 1.3 }}>{lesson.topic}</div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// Centered read-only preview shown when a lesson is clicked.
function LessonPreview({ lesson, subjects, classes, lessonTimes, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!lesson) return null;
  const s = getSubjectStyle(lesson.subjectId, subjects);
  const si  = lesson.slotIndex ?? 0;
  const dur = lesson.duration || 1;
  const t0  = lessonTimes[si];
  const t1  = lessonTimes[si + dur - 1];
  const timeLabel = t0 ? `${t0.start} — ${(t1 || t0).end}` : (dur >= 2 ? `${si + 1}–${si + 2}` : `${si + 1}`);
  const dayLabel = new Date(lesson.date + 'T12:00:00').toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit' });

  return (
    <div
      data-no-print
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--paper)', borderRadius: 'var(--r-l)', borderLeft: `4px solid ${s.ink}`,
          maxWidth: 520, width: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: 'var(--sh-3)',
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <SubjectChip subjectId={lesson.subjectId} subjects={subjects} />
              <ClassBadge classId={lesson.classId} classes={classes} size="md" />
            </div>
            <h2 className="display" style={{ fontSize: 22, margin: 0, color: 'var(--ink-1)' }}>{lesson.topic}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Schliessen"
            style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <X size={22} />
          </button>
        </div>
        <div style={{ padding: '18px 24px 24px' }}>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16, textTransform: 'capitalize' }}>
            {dayLabel} · {timeLabel}
          </div>
          {lesson.materials
            ? (
              <div className="markdown-body" style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                <ReactMarkdown>{lesson.materials}</ReactMarkdown>
              </div>
            )
            : <div style={{ fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>Kein Material hinterlegt.</div>}
        </div>
      </div>
    </div>
  );
}

// One Tagesplan lesson entry. Passed lessons (today, already ended) render as a
// slim collapsed row until hovered or clicked; active/future lessons render full.
function TagesplanLesson({ lesson, idx, subjects, classes, lessonTimes, isMobile, isActive, isPast, isGhost, expanded, onToggle }) {
  const [hover, setHover] = useState(false);
  const showFull = !isPast || expanded || hover;

  const s   = getSubjectStyle(lesson.subjectId, subjects);
  const si  = lesson.slotIndex ?? idx;
  const dur = lesson.duration || 1;
  const t0  = lessonTimes[si];
  const t1  = lessonTimes[si + dur - 1];
  const subjectName = subjects.find(sub => sub.id === lesson.subjectId)?.name || 'Allgemein';
  const className   = classes.find(c => c.id === lesson.classId)?.name;

  // Ghost (recurring-schedule preview): subject + time + class only, no content.
  if (isGhost) {
    return (
      <div style={{
        padding: 0, display: 'flex', alignItems: 'stretch', overflow: 'hidden',
        borderRadius: 'var(--r-m)', border: `1px dashed color-mix(in srgb, ${s.ink} 45%, var(--line))`,
        borderLeft: `3px solid color-mix(in srgb, ${s.ink} 55%, transparent)`, opacity: 0.85,
      }}>
        <div style={{
          width: isMobile ? 84 : 118, flexShrink: 0,
          background: `color-mix(in srgb, ${s.tint} 60%, var(--paper))`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '14px 8px', textAlign: 'center',
        }}>
          <div style={{ fontWeight: 700, fontSize: isMobile ? 12 : 14, color: s.ink, lineHeight: 1.15 }}>{subjectName}</div>
          <div className="mono" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: isMobile ? 11 : 13, color: 'var(--ink-2)' }}>
            {t0
              ? <><span>{t0.start}</span><span style={{ color: 'var(--ink-3)' }}>{(t1 || t0).end}</span></>
              : <span>{dur >= 2 ? `${si + 1}–${si + 2}` : si + 1}</span>}
          </div>
          {className && (
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', letterSpacing: '0.03em' }}>{className}</div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: '14px 18px', display: 'flex', alignItems: 'center' }}>
          <span className="mono" style={{
            fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Stundenplan</span>
        </div>
      </div>
    );
  }
  const pastHandlers = isPast ? {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onClick: onToggle,
  } : {};

  if (!showFull) {
    // Collapsed / passed row
    return (
      <div
        className="card-flat"
        {...pastHandlers}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
          borderLeft: `3px solid ${s.ink}`, opacity: 0.55, cursor: 'pointer',
        }}
      >
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0 }}>
          {t0 ? `${t0.start}–${(t1 || t0).end}` : (dur >= 2 ? `${si + 1}–${si + 2}` : `${si + 1}`)}
        </span>
        <span style={{ fontWeight: 600, fontSize: 13, color: s.ink, flexShrink: 0 }}>{subjectName}</span>
        {className && <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>{className}</span>}
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          · {lesson.topic}
        </span>
      </div>
    );
  }

  return (
    <div
      className="card-flat"
      {...pastHandlers}
      style={{
        padding: 0, display: 'flex', alignItems: 'stretch', overflow: 'hidden',
        borderLeft: `3px solid ${s.ink}`, cursor: isPast ? 'pointer' : undefined,
      }}
    >
      <div style={{
        width: isMobile ? 84 : 118, flexShrink: 0,
        background: s.tint,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '16px 8px', textAlign: 'center',
      }}>
        <div style={{ fontWeight: 800, fontSize: isMobile ? 12 : 14, color: s.ink, lineHeight: 1.15 }}>
          {subjectName}
        </div>
        <div className="mono" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          fontSize: isMobile ? 11 : 13, color: 'var(--ink-1)',
        }}>
          {t0
            ? <><span>{t0.start}</span><span style={{ color: 'var(--ink-3)' }}>{(t1 || t0).end}</span></>
            : <span>{dur >= 2 ? `${si + 1}–${si + 2}` : si + 1}</span>}
        </div>
        {className && (
          <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', letterSpacing: '0.03em' }}>
            {className}
          </div>
        )}
      </div>
      <div style={{
        flex: 1, minWidth: 0, padding: '16px 18px',
        background: isActive ? `color-mix(in srgb, ${s.ink} 15%, white)` : undefined,
      }}>
        {isActive && (
          <span style={{
            display: 'inline-block', marginBottom: 6, padding: '2px 9px', borderRadius: 100,
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            background: '#d20f39', color: '#fff',
          }}>Jetzt</span>
        )}
        <div style={{ fontWeight: 600, fontSize: 15, color: isActive ? '#1e1e2e' : 'var(--ink-1)', marginBottom: 4 }}>{lesson.topic}</div>
        {lesson.materials && (
          <div className="markdown-body" style={{ fontSize: 13, color: isActive ? '#3a3a4a' : 'var(--ink-3)' }}>
            <ReactMarkdown>{lesson.materials}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

function PublicSchedule() {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const { selectedWeek } = useWeekNav();

  const [subjects,    setSubjects]    = useState([]);
  const [allLessons,  setAllLessons]  = useState([]);
  const [lessonTimes, setLessonTimes] = useState([]);
  const [classes,      setClasses]      = useState([]);
  const [scheduleEntries, setScheduleEntries] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const toggleClass = id => setSelectedClasses(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const toggleSubject = id => setSelectedSubjects(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [activeTab,  setActiveTab]  = useState('tagesplan');
  const [selectedDay, setSelectedDay] = useState(() => {
    const dow = new Date().getDay();        // 0=Sun … 6=Sat
    return dow >= 1 && dow <= 5 ? dow - 1 : 0;
  });
  const [previewLesson, setPreviewLesson] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [expandedPast, setExpandedPast] = useState([]);
  const toggleExpandPast = id => setExpandedPast(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    axios.get('/api/admin/subjects').then(r => setSubjects(r.data)).catch(() => setSubjects([]));
    axios.get('/api/lessons').then(r => setAllLessons(r.data)).catch(() => setAllLessons([]));
    axios.get('/api/settings').then(r => setLessonTimes(r.data.lessonTimes || [])).catch(() => setLessonTimes([]));
    axios.get('/api/admin/classes').then(r => setClasses([...r.data].sort((a, b) => a.name.localeCompare(b.name, 'de')))).catch(() => setClasses([]));
    axios.get('/api/schedule').then(r => setScheduleEntries(r.data)).catch(() => setScheduleEntries([]));
  }, []);

  const weekDates = getWeekDates(selectedWeek);
  const weekISOs = weekDates.map(toISO);
  const selectedDate = toISO(weekDates[selectedDay]);
  // No boxes checked → show all; otherwise a lesson matches if it's assigned to a
  // checked class OR has no class ("Alle Klassen" — shown regardless of filter).
  const classMatch = l => selectedClasses.length === 0 || !l.classId || selectedClasses.includes(l.classId);
  const subjectMatch = l => selectedSubjects.length === 0 || selectedSubjects.includes(l.subjectId);
  const matches = l => classMatch(l) && subjectMatch(l);

  // Empty week (no real lessons anywhere in it) → show the recurring schedule as
  // content-free "ghost" entries (union of all teachers), filtered like real lessons.
  const weekEmpty = !allLessons.some(l => weekISOs.includes(l.date));
  const ghostLessons = weekEmpty
    ? scheduleEntries
        .map(e => ({
          id: 'g-' + e.id, subjectId: e.subjectId, classId: e.classId || null,
          duration: e.duration || 1, slotIndex: e.slotIndex, dayIndex: e.dayIndex,
          date: weekISOs[e.dayIndex], isGhost: true,
        }))
        .filter(matches)
    : [];

  const realLessons = allLessons.filter(l => l.date === selectedDate && matches(l));
  const lessons = weekEmpty
    ? ghostLessons.filter(g => g.dayIndex === selectedDay).sort((a, b) => a.slotIndex - b.slotIndex)
    : realLessons;

  // Week grid: assign dayIndex + gap-free slotIndex (legacy lessons without a stored slot fall back to createdAt order).
  const realGridLessons = (() => {
    const dates = weekDates.map(toISO);
    const raw = allLessons.filter(l => dates.includes(l.date) && matches(l));
    const byDay = {};
    raw.forEach(l => { (byDay[l.date] = byDay[l.date] || []).push(l); });
    const mapped = [];
    for (const dateKey of Object.keys(byDay)) {
      const dayLessons = byDay[dateKey];
      const occupied = new Set(dayLessons.filter(l => l.slotIndex !== undefined).map(l => l.slotIndex));
      const legacy = dayLessons
        .filter(l => l.slotIndex === undefined)
        .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
      let next = 0;
      for (const l of legacy) {
        while (occupied.has(next)) next++;
        mapped.push({ ...l, dayIndex: dates.indexOf(dateKey), slotIndex: next });
        occupied.add(next++);
      }
      for (const l of dayLessons.filter(l => l.slotIndex !== undefined)) {
        mapped.push({ ...l, dayIndex: dates.indexOf(dateKey) });
      }
    }
    return mapped;
  })();
  const gridLessons = weekEmpty ? ghostLessons : realGridLessons;
  const baseSlots = lessonTimes.length || parseInt(localStorage.getItem('maxLessons') || '9', 10);
  const slotCount = Math.max(baseSlots, ...gridLessons.map(l => (l.slotIndex ?? 0) + (l.duration || 1)), 1);

  // Only offer subjects that actually occur in the loaded lessons or the schedule.
  const presentSubjects = subjects.filter(s =>
    allLessons.some(l => l.subjectId === s.id) || scheduleEntries.some(e => e.subjectId === s.id));

  // Active (current/next) lesson — only for today, and only on the current week.
  const todayISO = toISO(now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const activeTagesId = (!weekEmpty && selectedDate === todayISO) ? activeLessonId(lessons, lessonTimes, now) : null;
  const todayIdx = weekDates.findIndex(d => toISO(d) === todayISO);
  const activeGridId = (!weekEmpty && todayIdx >= 0)
    ? activeLessonId(gridLessons.filter(l => l.dayIndex === todayIdx), lessonTimes, now)
    : null;

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 57px)', padding: isMobile ? '20px 16px 60px' : '32px 40px 60px' }}>

      {/* ── Tab row: views + filter dropdowns, one bubble ────────────────── */}
      <div className="tab-row" style={{ marginBottom: 28, flexWrap: 'wrap', gap: '4px 2px' }}>
        <button className={activeTab === 'tagesplan'    ? 'active' : ''} onClick={() => { setActiveTab('tagesplan'); setSelectedSubjects([]); }}>Tagesplan</button>
        <button className={activeTab === 'wochenraster' ? 'active' : ''} onClick={() => setActiveTab('wochenraster')}>Wochenraster</button>
        {classes.length > 0 && (
          <FilterDropdown
            label="Klassen"
            options={classes}
            selected={selectedClasses}
            onToggle={toggleClass}
            colorFor={id => getClassStyle(id, classes)?.ink || 'var(--accent)'}
          />
        )}
        {activeTab === 'wochenraster' && presentSubjects.length > 0 && (
          <FilterDropdown
            label="Fächer"
            options={presentSubjects}
            selected={selectedSubjects}
            onToggle={toggleSubject}
            colorFor={id => getSubjectStyle(id, subjects).ink}
          />
        )}
      </div>

      {/* ── Schedule-preview hint (empty week) ───────────────────────────── */}
      {weekEmpty && ghostLessons.length > 0 && (
        <div style={{
          marginBottom: 20, padding: '10px 14px', borderRadius: 'var(--r-m)',
          border: '1px dashed var(--line-strong)', background: 'var(--surface)',
          fontSize: 13, color: 'var(--ink-3)',
        }}>
          Stundenplan — für diese Woche sind noch keine Lektionen erfasst.
        </div>
      )}

      {/* ── Tagesplan ────────────────────────────────────────────────────── */}
      {activeTab === 'tagesplan' && (
        <div>
          {/* ── Day pills ────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: isMobile ? 6 : 12, marginBottom: 24 }}>
            {DAYS_LONG.map((dayName, i) => {
              const active = i === selectedDay;
              const count  = weekEmpty
                ? ghostLessons.filter(g => g.dayIndex === i).length
                : allLessons.filter(l => l.date === toISO(weekDates[i]) && matches(l)).length;
              return (
                <button
                  key={dayName}
                  aria-pressed={active}
                  onClick={() => setSelectedDay(i)}
                  style={{
                    flex: 1, padding: isMobile ? '10px 4px' : '14px 18px', textAlign: 'center',
                    background: active ? 'var(--ink-1)' : 'var(--paper)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r-l)',
                    color: active ? 'var(--bg)' : 'var(--ink-1)',
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s',
                    boxShadow: active ? 'var(--sh-2)' : 'none',
                  }}
                >
                  <div className="mono" style={{
                    fontSize: '0.81rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: active ? undefined : 'var(--ink-3)',
                    opacity: active ? 0.65 : 1,
                  }}>
                    {formatDate(weekDates[i])}
                  </div>
                  <div className="display" style={{ fontSize: isMobile ? '1.06rem' : '1.375rem', marginTop: 4, color: 'inherit' }}>
                    {isMobile ? dayName.slice(0, 2) : dayName}
                  </div>
                  <div style={{ fontSize: '0.875rem', marginTop: 6, color: active ? undefined : 'var(--ink-3)', opacity: active ? 0.65 : 1 }}>
                    {count} {isMobile ? 'L' : `Lektion${count !== 1 ? 'en' : ''}`}
                  </div>
                </button>
              );
            })}
          </div>
          {lessons.length === 0
            ? <Empty text={`Keine Lektionen für ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit' })}.`} />
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lessons.map((lesson, idx) => {
                  const si = lesson.slotIndex ?? idx;
                  const dur = lesson.duration || 1;
                  const endT = lessonTimes[si + dur - 1] || lessonTimes[si];
                  const isGhost = lesson.isGhost;
                  const isActive = !isGhost && lesson.id === activeTagesId;
                  const isPast = !isGhost && selectedDate === todayISO && !isActive && !!endT && toMin(endT.end) <= nowMin;
                  return (
                    <TagesplanLesson
                      key={lesson.id}
                      lesson={lesson} idx={idx}
                      subjects={subjects} classes={classes} lessonTimes={lessonTimes} isMobile={isMobile}
                      isActive={isActive} isPast={isPast} isGhost={isGhost}
                      expanded={expandedPast.includes(lesson.id)}
                      onToggle={() => toggleExpandPast(lesson.id)}
                    />
                  );
                })}
              </div>
            )
          }
        </div>
      )}

      {/* ── Wochenraster ─────────────────────────────────────────────────── */}
      {activeTab === 'wochenraster' && (
        gridLessons.length === 0
          ? <Empty text="Keine Lektionen diese Woche." />
          : (
            <WeekGrid
              gridLessons={gridLessons} weekDates={weekDates} subjects={subjects} classes={classes}
              slotCount={slotCount} lessonTimes={lessonTimes} isMobile={isMobile}
              onSelectLesson={setPreviewLesson} activeId={activeGridId}
            />
          )
      )}

      {previewLesson && (
        <LessonPreview
          lesson={previewLesson}
          subjects={subjects}
          classes={classes}
          lessonTimes={lessonTimes}
          onClose={() => setPreviewLesson(null)}
        />
      )}
    </div>
  );
}

export default PublicSchedule;
